import React, {useEffect, useRef} from "react";
import type {Ring} from "./wm";

interface Props {
  wmPolygon: Ring[];
  // Viv viewState: {target: [x, y, z], zoom: number} in L0 image px.
  viewState: any;
  canvasW: number;
  canvasH: number;
  // Display tuning (from the WM gear popover).
  dim: number;         // 0..1 — opacity of the scrim over non-WM tissue
  edgeColor: string;   // "#RRGGBB" boundary stroke
  edgeWidth: number;   // px boundary stroke width
}

/**
 * Full-canvas white-matter isolation overlay, drawn in screen space and kept
 * in lock-step with the Viv DetailView via its viewState.
 *
 * We deliberately do NOT route this through deck.gl (deckProps.layers): custom
 * layers handed to VivViewer silently fail to render, which is why toggling the
 * old PolygonLayer did nothing on the main image. The minimap + drag-rect + HUD
 * all draw into their own screen-space canvases for exactly this reason, so we
 * follow that proven pattern here.
 *
 * "Isolate" means: dim everything OUTSIDE white matter, punch the WM region
 * fully clear, and draw a crisp boundary. Gray-matter islands enclosed by WM
 * are cut back out via the even-odd fill rule (each ring is an independent
 * boundary, so a hole inside the outer ring stays dimmed), so the reviewer sees
 * only the true WM parenchyma at full brightness.
 */
export function WmOverlay(
    {wmPolygon, viewState, canvasW, canvasH, dim, edgeColor, edgeWidth}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = Math.round(canvasW * dpr);
    cv.height = Math.round(canvasH * dpr);
    cv.style.width = `${canvasW}px`;
    cv.style.height = `${canvasH}px`;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvasW, canvasH);

    if (!viewState || !Array.isArray(viewState.target)) return;
    const zoom = Array.isArray(viewState.zoom) ? viewState.zoom[0] : viewState.zoom;
    const z = Math.pow(2, zoom);              // screen px per image px
    const [tx, ty] = viewState.target;        // image px at viewport center
    // Inverse of screenToImage(): image px -> container-local screen px.
    const toX = (x: number) => (x - tx) * z + canvasW / 2;
    const toY = (y: number) => (y - ty) * z + canvasH / 2;

    // One combined path for every ring (decimated for cheap redraws while
    // panning). Even-odd fill turns enclosed rings into holes automatically.
    const path = new Path2D();
    for (const ring of wmPolygon) {
      if (!ring || ring.length < 3) continue;
      const step = Math.max(1, Math.floor(ring.length / 1400));
      path.moveTo(toX(ring[0][0]), toY(ring[0][1]));
      for (let i = step; i < ring.length; i += step) {
        path.lineTo(toX(ring[i][0]), toY(ring[i][1]));
      }
      path.closePath();
    }

    // 1. Dim the whole frame (strength from the gear popover), then erase the
    //    scrim inside WM so the tissue there reads at full brightness.
    //    destination-out + even-odd keeps gray-matter islands dimmed.
    const a = Math.max(0, Math.min(1, dim));
    if (a > 0) {
      ctx.fillStyle = `rgba(3, 6, 16, ${a})`;
      ctx.fillRect(0, 0, canvasW, canvasH);
      ctx.save();
      ctx.globalCompositeOperation = "destination-out";
      ctx.fill(path, "evenodd");
      ctx.restore();
    }

    // 2. Crisp boundary on top so the edge is unmistakable at any zoom.
    if (edgeWidth > 0) {
      ctx.lineJoin = "round";
      ctx.lineWidth = edgeWidth;
      ctx.strokeStyle = edgeColor;
      ctx.stroke(path);
    }
  }, [wmPolygon, viewState, canvasW, canvasH, dim, edgeColor, edgeWidth]);

  return (
    <canvas
      ref={ref}
      style={{
        position: "absolute", left: 0, top: 0, zIndex: 4,
        pointerEvents: "none",
      }}
    />
  );
}
